import React, { useState } from 'react';
import './help.css';
import { cardStyle, palette, button } from '../styles/ui';
import { useModalEscape } from '../utils/useModalEscape';

export default function HelpModal({ open, onClose }) {
  const [activeTab, setActiveTab] = useState('inicio');

  useModalEscape(open, onClose);

  if (!open) return null;

  const headerStyle = {
    background: 'linear-gradient(135deg, ' + palette.indigo + ' 0%, ' + palette.purple + ' 100%)',
    color: 'white',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  };

  const modalInnerStyle = {
    ...cardStyle,
    borderRadius: 12,
    padding: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  };

  const closeBtnStyle = {
    border: 'none',
    background: 'rgba(255,255,255,0.2)',
    color: 'white',
    fontSize: 24,
    cursor: 'pointer',
    borderRadius: '50%',
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  };

  const tabsContainerStyle = {
    display: 'flex',
    gap: 4,
    padding: '0 20px',
    background: '#f7fafc',
    borderBottom: '2px solid #e2e8f0',
  };

  const tabStyle = (isActive) => ({
    padding: '12px 20px',
    border: 'none',
    background: isActive ? 'white' : 'transparent',
    color: isActive ? palette.indigo : '#64748b',
    fontWeight: isActive ? '600' : '500',
    fontSize: 14,
    cursor: 'pointer',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottom: isActive ? '2px solid ' + palette.indigo : '2px solid transparent',
    marginBottom: -2,
    transition: 'all 0.2s',
  });

  const badgeStyle = (color = 'blue') => ({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: '600',
    marginLeft: 8,
    background: color === 'blue' ? '#dbeafe' : color === 'green' ? '#d1fae5' : '#fef3c7',
    color: color === 'blue' ? '#1e40af' : color === 'green' ? '#065f46' : '#92400e',
  });

  const sectionStyle = {
    marginBottom: 24,
    padding: 20,
    background: 'white',
    borderRadius: 12,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  };

  const iconTitleStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  };

  const kbdStyle = {
    display: 'inline-block',
    padding: '2px 6px',
    background: '#f1f5f9',
    border: '1px solid #cbd5e1',
    borderRadius: 4,
    fontSize: 12,
    fontFamily: 'monospace',
    fontWeight: '600',
  };

  return (
    <div className="help-backdrop" role="dialog" aria-modal="true">
      <div className="help-modal" style={{ maxWidth: 1000, width: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={modalInnerStyle}>
          <header style={headerStyle}>
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: '700' }}>🎯 Guía Completa de Ventio</h2>
              <p style={{ margin: '4px 0 0', fontSize: 13, opacity: 0.9 }}>Todo lo que necesitás saber para empezar</p>
            </div>
            <button
              aria-label="Cerrar ayuda"
              style={closeBtnStyle}
              onClick={onClose}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
            >
              ×
            </button>
          </header>

          <div style={tabsContainerStyle}>
            <button
              style={tabStyle(activeTab === 'inicio')}
              onClick={() => setActiveTab('inicio')}
            >
              🚀 Inicio
            </button>
            <button
              style={tabStyle(activeTab === 'operaciones')}
              onClick={() => setActiveTab('operaciones')}
            >
              💼 Operaciones
            </button>
            <button
              style={tabStyle(activeTab === 'avanzado')}
              onClick={() => setActiveTab('avanzado')}
            >
              ⚙️ Avanzado
            </button>
          </div>

          <div style={{ padding: 20, overflow: 'auto', flex: 1 }}>
            {activeTab === 'inicio' && (
              <div>
                {/* Inicio Rápido */}
                <div style={sectionStyle}>
                  <div style={iconTitleStyle}>
                    <span style={{ fontSize: 32 }}>🎯</span>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 18, color: '#1e293b' }}>Primeros Pasos</h3>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Configurá tu sistema en 5 minutos</p>
                    </div>
                  </div>
                  <ol style={{ paddingLeft: 20, lineHeight: 1.8 }}>
                    <li>
                      <strong>✅ Completar datos de tu empresa</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        Ve a <em>Configuración → Empresa</em> y completa razón social, CUIT, dirección y teléfono.
                      </p>
                    </li>
                    <li>
                      <strong>📦 Crear tus primeros productos</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        En <em>Productos → Nuevo producto</em>, agrega nombre, precio y stock inicial. Podés importar desde CSV si tenés muchos.
                      </p>
                    </li>
                    <li>
                      <strong>🏪 Configurar punto de venta</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        En <em>Configuración → Fiscal</em>, configura tu punto de venta AFIP si vas a facturar electrónicamente.
                      </p>
                    </li>
                    <li>
                      <strong>🛒 Hacer una venta de prueba</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        Ve a <em>Ventas</em>, busca un producto, agregalo al carrito y finaliza la venta para familiarizarte con el proceso.
                      </p>
                    </li>
                    <li>
                      <strong>📊 Revisar tus reportes</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        En <em>Reportes</em> podés ver ventas del día, productos más vendidos y estadísticas.
                      </p>
                    </li>
                  </ol>
                </div>

                {/* Planes y Pagos */}
                <div style={sectionStyle}>
                  <div style={iconTitleStyle}>
                    <span style={{ fontSize: 32 }}>💳</span>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 18, color: '#1e293b' }}>
                        Planes y Pagos
                        <span style={badgeStyle('green')}>Nuevo</span>
                      </h3>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Información sobre suscripciones</p>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ margin: '0 0 8px', fontSize: 15, color: '#334155' }}>📋 Planes Disponibles</h4>
                    <ul style={{ paddingLeft: 20, lineHeight: 1.8 }}>
                      <li><strong>Starter (Gratis):</strong> 50 ventas/mes, 2 usuarios, 30 días de prueba</li>
                      <li><strong>Pro:</strong> Ventas ilimitadas, usuarios ilimitados, todas las funciones</li>
                    </ul>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ margin: '0 0 8px', fontSize: 15, color: '#334155' }}>💰 Cómo Actualizar al Plan Pro</h4>
                    <ol style={{ paddingLeft: 20, lineHeight: 1.8 }}>
                      <li>Ve a <em>Configuración → Planes</em> o hacé clic en "Actualizar" en el banner</li>
                      <li>Elegí pago mensual o anual (ahorrás con el plan anual)</li>
                      <li>Completá el pago con Mercado Pago (tarjeta de crédito/débito)</li>
                      <li>Tu plan se activa automáticamente después del pago</li>
                    </ol>
                  </div>

                  <div style={{ background: '#f0fdf4', padding: 12, borderRadius: 8, border: '1px solid #86efac' }}>
                    <p style={{ margin: 0, fontSize: 13, color: '#166534' }}>
                      <strong>💡 Tip:</strong> El pago anual te da un descuento significativo. Podés ver el ahorro exacto en la página de planes.
                    </p>
                  </div>
                </div>

                {/* Soporte */}
                <div style={sectionStyle}>
                  <div style={iconTitleStyle}>
                    <span style={{ fontSize: 32 }}>📞</span>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 18, color: '#1e293b' }}>Soporte y Ayuda</h3>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Estamos para ayudarte</p>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                    <div style={{ padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: 20, marginBottom: 4 }}>📱</div>
                      <strong style={{ fontSize: 13 }}>WhatsApp</strong>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>+54 11 3669-5969</p>
                    </div>
                    <div style={{ padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: 20, marginBottom: 4 }}>📧</div>
                      <strong style={{ fontSize: 13 }}>Email</strong>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>ventiopos@gmail.com</p>
                    </div>
                    <div style={{ padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: 20, marginBottom: 4 }}>🎫</div>
                      <strong style={{ fontSize: 13 }}>Tickets</strong>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Botón naranja en la app</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'operaciones' && (
              <div>
                {/* Ventas */}
                <div style={sectionStyle}>
                  <div style={iconTitleStyle}>
                    <span style={{ fontSize: 32 }}>🛒</span>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 18, color: '#1e293b' }}>Ventas</h3>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Cómo realizar una venta paso a paso</p>
                    </div>
                  </div>

                  <ol style={{ paddingLeft: 20, lineHeight: 1.8 }}>
                    <li>
                      <strong>Buscar productos</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        Escribí el nombre o escaneá el código de barras. Presioná <span style={kbdStyle}>Enter</span> para agregar al carrito.
                      </p>
                    </li>
                    <li>
                      <strong>Ajustar cantidades</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        En la lista de items podés cambiar la cantidad, aplicar descuentos (% o monto fijo).
                      </p>
                    </li>
                    <li>
                      <strong>Seleccionar cliente (opcional)</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        Si el cliente está registrado, buscalo para asociar la venta. Necesario para facturar.
                      </p>
                    </li>
                    <li>
                      <strong>Elegir forma de pago</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        Efectivo, tarjeta, transferencia o combinación. Podés dividir el pago en múltiples métodos.
                      </p>
                    </li>
                    <li>
                      <strong>Finalizar venta</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        Hacé clic en "Finalizar" o presioná <span style={kbdStyle}>F9</span>. Se genera el comprobante automáticamente.
                      </p>
                    </li>
                  </ol>

                  <div style={{ background: '#eff6ff', padding: 12, borderRadius: 8, border: '1px solid #93c5fd', marginTop: 12 }}>
                    <p style={{ margin: 0, fontSize: 13, color: '#1e40af' }}>
                      <strong>⌨️ Atajo rápido:</strong> Presioná <span style={kbdStyle}>Ctrl</span> + <span style={kbdStyle}>F</span> para enfocar el buscador de productos.
                    </p>
                  </div>
                </div>

                {/* Cuotas e Intereses */}
                <div style={sectionStyle}>
                  <div style={iconTitleStyle}>
                    <span style={{ fontSize: 32 }}>💳</span>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 18, color: '#1e293b' }}>
                        Cuotas e Intereses
                        <span style={badgeStyle('green')}>Nuevo</span>
                      </h3>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Financiación para ventas con tarjeta</p>
                    </div>
                  </div>

                  <ol style={{ paddingLeft: 20, lineHeight: 1.8 }}>
                    <li>
                      <strong>Seleccionar Tarjeta</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        Al elegir "Tarjeta" como medio de pago, aparecerán nuevas opciones.
                      </p>
                    </li>
                    <li>
                      <strong>Elegir cuotas</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        Seleccioná la cantidad de cuotas (1 a 12).
                      </p>
                    </li>
                    <li>
                      <strong>Aplicar interés</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        Ingresá el % de interés por cuota. El sistema calculará automáticamente el recargo.
                      </p>
                    </li>
                    <li>
                      <strong>Ver detalles</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        Se mostrará el "Total con interés" y el valor de cada cuota antes de confirmar.
                      </p>
                    </li>
                  </ol>

                  <div style={{ background: '#fffbeb', padding: 12, borderRadius: 8, border: '1px solid #fcd34d', marginTop: 12 }}>
                    <p style={{ margin: 0, fontSize: 13, color: '#92400e' }}>
                      <strong>📝 Nota:</strong> El recargo por financiación solo se detalla en las Facturas Fiscales (AFIP). En los tickets de control interno (no fiscales) figura el precio original.
                    </p>
                  </div>
                </div>

                {/* Productos */}
                <div style={sectionStyle}>
                  <div style={iconTitleStyle}>
                    <span style={{ fontSize: 32 }}>📦</span>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 18, color: '#1e293b' }}>Productos</h3>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Gestión de inventario</p>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ margin: '0 0 8px', fontSize: 15, color: '#334155' }}>Crear Producto</h4>
                    <ol style={{ paddingLeft: 20, lineHeight: 1.8 }}>
                      <li>Ve a <em>Productos → Nuevo producto</em></li>
                      <li>Completa: nombre, código/SKU, precio de venta, costo, stock inicial</li>
                      <li>Opcionalmente agrega categoría, imagen y descripción</li>
                      <li>Guarda y el producto estará disponible para vender</li>
                    </ol>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ margin: '0 0 8px', fontSize: 15, color: '#334155' }}>Importar desde CSV</h4>
                    <ol style={{ paddingLeft: 20, lineHeight: 1.8 }}>
                      <li>Descarga la plantilla CSV desde <em>Productos → Importar</em></li>
                      <li>Completa: sku, nombre, precio, costo, stock, categoría</li>
                      <li>Sube el archivo y mapea las columnas</li>
                      <li>Revisa la vista previa y confirma la importación</li>
                    </ol>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ margin: '0 0 8px', fontSize: 15, color: '#334155' }}>⚖️ Productos Pesables / Balanzas (EAN-13)</h4>
                    <p style={{ margin: '0 0 8px', fontSize: 14, color: '#64748b' }}>
                      Las balanzas comerciales (Systel, Kretz, Moretti, Bizerba) generan etiquetas con el formato <code>20 + PLU (5 dígitos) + PESO en gramos (5 dígitos)</code>.
                    </p>
                    <ol style={{ paddingLeft: 20, lineHeight: 1.8, fontSize: 14 }}>
                      <li>Al crear el producto en Ventio, poné en <strong>Código de Barras</strong> únicamente el número de PLU (ej: <code>15</code> o <code>00015</code>) y el precio por kilo.</li>
                      <li>Al escanear la etiqueta en el Punto de Venta (ej: <code>2000015025004</code>), Ventio detectará automáticamente el PLU 15, calculará los 2.5 kg y agregará el monto exacto al carrito.</li>
                    </ol>
                  </div>

                  <div style={{ background: '#fef3c7', padding: 12, borderRadius: 8, border: '1px solid #fcd34d' }}>
                    <p style={{ margin: 0, fontSize: 13, color: '#92400e' }}>
                      <strong>⚠️ Importante:</strong> El SKU debe ser único por producto. Si importás un SKU existente, se actualizará el producto.
                    </p>
                  </div>
                </div>

                {/* Clientes */}
                <div style={sectionStyle}>
                  <div style={iconTitleStyle}>
                    <span style={{ fontSize: 32 }}>👥</span>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 18, color: '#1e293b' }}>Clientes</h3>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Gestión de base de clientes</p>
                    </div>
                  </div>

                  <ol style={{ paddingLeft: 20, lineHeight: 1.8 }}>
                    <li>
                      <strong>Crear cliente</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        Ve a <em>Clientes → Nuevo cliente</em>. Completa nombre, email, teléfono y documento.
                      </p>
                    </li>
                    <li>
                      <strong>Datos fiscales (para facturar)</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        Si vas a emitir facturas, completa CUIT, razón social y condición fiscal (IVA Responsable Inscripto, Monotributo, etc).
                      </p>
                    </li>
                    <li>
                      <strong>Historial de compras</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        Cada cliente tiene un historial automático de todas sus compras y facturas.
                      </p>
                    </li>
                  </ol>
                </div>

                {/* Caja */}
                <div style={sectionStyle}>
                  <div style={iconTitleStyle}>
                    <span style={{ fontSize: 32 }}>💰</span>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 18, color: '#1e293b' }}>Caja</h3>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Control de efectivo diario</p>
                    </div>
                  </div>

                  <ol style={{ paddingLeft: 20, lineHeight: 1.8 }}>
                    <li>
                      <strong>Apertura de caja</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        Al inicio del día, registra el monto inicial en <em>Caja → Apertura</em>.
                      </p>
                    </li>
                    <li>
                      <strong>Registrar movimientos</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        Ingresos extras (ej: pago de deuda) o egresos (ej: compra de insumos, retiro de efectivo).
                      </p>
                    </li>
                    <li>
                      <strong>Cierre y arqueo</strong>
                      <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                        Al final del día, hace el cierre. Contá el efectivo físico y compará con el sistema.
                      </p>
                    </li>
                  </ol>

                  <div style={{ background: '#f0fdf4', padding: 12, borderRadius: 8, border: '1px solid #86efac', marginTop: 12 }}>
                    <p style={{ margin: 0, fontSize: 13, color: '#166534' }}>
                      <strong>💡 Tip:</strong> Las ventas en efectivo se registran automáticamente en la caja. Solo necesitás registrar movimientos extras.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'avanzado' && (
              <div>
                {/* Reportes */}
                <div style={sectionStyle}>
                  <div style={iconTitleStyle}>
                    <span style={{ fontSize: 32 }}>📊</span>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 18, color: '#1e293b' }}>Reportes</h3>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Análisis de tu negocio</p>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ margin: '0 0 8px', fontSize: 15, color: '#334155' }}>Reportes Disponibles</h4>
                    <ul style={{ paddingLeft: 20, lineHeight: 1.8 }}>
                      <li><strong>Ventas por periodo:</strong> Totales diarios, semanales, mensuales con gráficos</li>
                      <li><strong>Productos más vendidos:</strong> Top 10 por cantidad o por ingresos</li>
                      <li><strong>Ventas por vendedor:</strong> Rendimiento de cada usuario</li>
                      <li><strong>Reporte de caja:</strong> Movimientos de efectivo del periodo</li>
                      <li><strong>Reporte fiscal:</strong> Datos para AFIP y tu contador</li>
                    </ul>
                  </div>

                  <div style={{ background: '#eff6ff', padding: 12, borderRadius: 8, border: '1px solid #93c5fd' }}>
                    <p style={{ margin: 0, fontSize: 13, color: '#1e40af' }}>
                      <strong>📥 Exportar:</strong> Todos los reportes se pueden exportar a Excel/CSV para análisis externo.
                    </p>
                  </div>
                </div>

                {/* Facturación */}
                <div style={sectionStyle}>
                  <div style={iconTitleStyle}>
                    <span style={{ fontSize: 32 }}>🧾</span>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 18, color: '#1e293b' }}>Facturación Electrónica</h3>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Integración con AFIP</p>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ margin: '0 0 8px', fontSize: 15, color: '#334155' }}>Configuración Inicial</h4>
                    <ol style={{ paddingLeft: 20, lineHeight: 1.8 }}>
                      <li>Ve a <em>Configuración → Fiscal</em></li>
                      <li>Completa CUIT, razón social y domicilio fiscal</li>
                      <li>Configura punto de venta AFIP</li>
                      <li>Sube certificado y clave privada (si tenés)</li>
                      <li>Selecciona ambiente (testing o producción)</li>
                    </ol>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ margin: '0 0 8px', fontSize: 15, color: '#334155' }}>Emitir Factura</h4>
                    <ol style={{ paddingLeft: 20, lineHeight: 1.8 }}>
                      <li>En la venta, selecciona el cliente (debe tener datos fiscales)</li>
                      <li>Elige tipo de comprobante (A, B o C según el cliente)</li>
                      <li>Revisa los datos y presiona "Emitir factura"</li>
                      <li>El sistema solicita CAE a AFIP automáticamente</li>
                      <li>Se genera el PDF con el comprobante</li>
                    </ol>
                  </div>

                  <div style={{ background: '#f0fdf4', padding: 12, borderRadius: 8, border: '1px solid #86efac' }}>
                    <p style={{ margin: 0, fontSize: 13, color: '#166534' }}>
                      <strong>💡 Tip:</strong> Las facturas no se pueden eliminar, solo anular con nota de crédito. Revisá bien los datos antes de emitir.
                    </p>
                  </div>
                </div>

                {/* Atajos de Teclado */}
                <div style={sectionStyle}>
                  <div style={iconTitleStyle}>
                    <span style={{ fontSize: 32 }}>⌨️</span>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 18, color: '#1e293b' }}>
                        Atajos de Teclado
                        <span style={badgeStyle('yellow')}>Pro Tip</span>
                      </h3>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Trabaja más rápido</p>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                    <div>
                      <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#334155' }}>Pantalla de Ventas</h4>
                      <ul style={{ paddingLeft: 0, listStyle: 'none', lineHeight: 2 }}>
                        <li><span style={kbdStyle}>F2</span> → Abrir modal de pago</li>
                        <li><span style={kbdStyle}>F7</span> → Ticket (No Fiscal) *</li>
                        <li><span style={kbdStyle}>F9</span> → Factura Fiscal *</li>
                        <li><span style={kbdStyle}>F10</span> → Consultar precio</li>
                        <li><span style={kbdStyle}>Enter</span> → Agregar al carrito</li>
                        <li><span style={kbdStyle}>Esc</span> → Cerrar modal</li>
                        <li><span style={kbdStyle}>↑</span> / <span style={kbdStyle}>↓</span> → Navegar sugerencias</li>
                      </ul>
                      <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b' }}>
                        * Solo cuando el modal de pago está abierto
                      </p>
                    </div>

                    <div>
                      <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#334155' }}>Flujo de Venta Rápido</h4>
                      <ol style={{ paddingLeft: 20, lineHeight: 1.8, fontSize: 13 }}>
                        <li>Escribí o escaneá el producto</li>
                        <li>Presioná <span style={kbdStyle}>Enter</span> para agregar</li>
                        <li>Repetí para más productos</li>
                        <li>Presioná <span style={kbdStyle}>F2</span> para pagar</li>
                        <li>Elegí método de pago</li>
                        <li>Presioná <span style={kbdStyle}>F7</span> (ticket) o <span style={kbdStyle}>F9</span> (factura)</li>
                      </ol>
                    </div>
                  </div>

                  <div style={{ background: '#eff6ff', padding: 12, borderRadius: 8, border: '1px solid #93c5fd', marginTop: 12 }}>
                    <p style={{ margin: 0, fontSize: 13, color: '#1e40af' }}>
                      <strong>💡 Tip:</strong> Usá <span style={kbdStyle}>F10</span> para consultar el precio de un producto sin agregarlo al carrito.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <footer style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
            <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>
              ¿Necesitás más ayuda? Contactanos por WhatsApp o email
            </p>
            <button
              style={{
                ...button('indigo'),
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: '600',
              }}
              onClick={onClose}
            >
              Entendido
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
}
